import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFirestore } from './useFirestore';
import { RootState } from '../store';
import { setChannels } from '../store/slices/playlistSlice';
import { saveChannelsLocally, loadChannelsLocally, clearLocalChannels } from '../utils/localStorageUtils';
import logger from '../utils/logger';

/**
 * Hook to manage playlist synchronization between local storage and Firestore
 * - For authenticated users: use Firestore
 * - For non-authenticated users: use local storage
 */
export const usePlaylistSync = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { channels } = useSelector((state: RootState) => state.playlist);
  const { loadPlaylist, savePlaylist } = useFirestore();
  
  // Use ref to track if migration has been done
  const hasMigratedRef = useRef(false);

  // Memoize the migration function
  const migrateToFirestore = useCallback(async (userId: string) => {
    if (hasMigratedRef.current) return;
    
    try {
      // Load existing Firestore playlist
      const firestoreChannels = await loadPlaylist(userId);
      
      // Load local channels
      const localChannels = loadChannelsLocally();
      
      if (localChannels.length > 0) {
        logger.info('Migrating local channels to Firestore', { count: localChannels.length });
        
        // Combine channels (prefer local if there's a conflict)
        const existingUrls = new Set(firestoreChannels.map(channel => channel.url));
        const newChannels = localChannels.filter(channel => !existingUrls.has(channel.url));
        
        const combinedChannels = [...firestoreChannels, ...newChannels];
        
        // Save combined channels to Firestore
        if (newChannels.length > 0) {
          await savePlaylist(userId, combinedChannels);
          logger.info('Migrated local channels to Firestore', { added: newChannels.length });
        }
        
        // Update Redux store
        dispatch(setChannels(combinedChannels));
        
        // Clear local storage since data is now in Firestore
        clearLocalChannels();
      } else if (firestoreChannels.length > 0) {
        // Just load Firestore channels if no local channels
        dispatch(setChannels(firestoreChannels));
      }
      
      hasMigratedRef.current = true;
    } catch (error) {
      logger.error('Failed to migrate local playlist to Firestore', error);
    }
  }, [dispatch, loadPlaylist, savePlaylist]);

  // When user logs in, migrate local playlist to Firestore if needed
  useEffect(() => {
    if (user?.uid && !hasMigratedRef.current) {
      migrateToFirestore(user.uid);
    }
  }, [user?.uid, migrateToFirestore]);

  // When user logs out, save current playlist to local storage
  useEffect(() => {
    if (!user && channels.length > 0) {
      logger.info('Saving playlist to local storage on logout', { count: channels.length });
      saveChannelsLocally(channels);
    }
  }, [user, channels]);

  return null;
}; 